import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ForwardOutlinedIcon from "@mui/icons-material/ForwardOutlined";
import { Button, IconButton, OutlinedInput, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux";
import AlertDialog from "../../../../ui/components/alert-dialog/alert-dialog";
import AlertMessage from "../../../../ui/components/alert-message/alert-message";
import Table from "../../../../ui/components/table/table";
import { UserInfo } from "../../../../ui/models/userInfo";
import { PrecargaSempreList } from "../../../models/precarga-sempre-list";
import { importacionSempreServices } from "../../../services/importacion-sempre.services";
import { getFormattedDate } from "../../../utils/common.utils";
import {
  initialFilter,
  queryParamsConfig,
} from "../../../utils/precarga-sempre.utils";
import { useQueryParamsReplace } from "../../../utils/useQueryParamsReplace";
import PracticaPresentarModal from "../practica-presentar/practica-presentar";
import "./precarga-sempre.scss";

interface PrecargaSempreListActionsProps {
  row: PrecargaSempreList;
  handlePresentarPractica: any;
  handleAnularPractica: any;
}

const PrecargaSempreListActions = (props: PrecargaSempreListActionsProps) => {
  const { row, handlePresentarPractica, handleAnularPractica } = props;
  return (
    <>
      {(row.observacion === null ||
        row.observacion ===
          "Existe más de un precio para ese código de práctica. ") && (
        <Tooltip title="Presentar">
          <IconButton
            className="button-presentar"
            aria-label="presentar"
            onClick={() => handlePresentarPractica(row)}
          >
            <ForwardOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Anular">
        <IconButton
          aria-label="anular"
          onClick={() => handleAnularPractica(row)}
        >
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      </Tooltip>
    </>
  );
};

export default function PrecargaSempre2() {
  const [queryParams, setQueryParams] =
    useQueryParamsReplace(queryParamsConfig);
  const [fetching, setFetching] = useState<boolean>(true);
  const location = useLocation();
  const { state, search } = location;
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [columns, setColumns] = useState<TableColumn<PrecargaSempreList>[]>([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [textAlertModal, setTextAlertModal] = useState<string>("");
  const [practicaSelected, setPracticaSelected] =
    useState<PrecargaSempreList>();
  const [showPresentarModal, setShowPresentarModal] = useState(false);
  const navigate = useNavigate();

  const initialColumns: TableColumn<PrecargaSempreList>[] = [
    {
      name: "Paciente",
      selector: (row) => row.afiliadoCuil + " - " + row.afiliadoNombre,
      sortable: true,
      sortField: "paciente",
      width: "20%",
    },
    {
      name: "Nro. Validación Online",
      selector: (row) => row.valOnlineNumero,
      sortable: true,
      sortField: "valonlinenumero",
      width: "8%",
    },
    {
      name: "Serie Número",
      selector: (row) =>
        ArmarSerieNumero(row.serieNumero, row.prescripcionNumero),
      sortable: true,
      sortField: "serienumero",
      width: "7%",
    },
    {
      name: "Código de Práctica",
      selector: (row) => row.codigoPractica,
      wrap: true,
      width: "7%",
    },
    {
      name: "Descripcion",
      selector: (row) => row.nombrePractica,
    },
    {
      name: "Fecha Consumo",
      selector: (row) => getFormattedDate(row.fechaConsumo.toString()),
      width: "8%",
    },
    {
      name: "Cantidad",
      selector: (row) => row.cantidad,
      width: "6%",
    },
    {
      name: "Valor",
      selector: (row) => Number.parseFloat(row.precio.toString()).toFixed(2),
      width: "8%",
    },
    {
      name: "Valor Total",
      selector: (row) =>
        Number.parseFloat((row.precio * row.cantidad).toString()).toFixed(2),
      wrap: true,
      width: "8%",
    },
    {
      name: "Observacion",
      selector: (row) => row.observacion,
      wrap: true,
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <PrecargaSempreListActions
          row={row}
          handlePresentarPractica={handlePresentarPractica}
          handleAnularPractica={handleAnularPractica}
        />
      ),
      width: "5%",
      style: {
        position: "sticky",
        right: 0,
        backgroundColor: "#fff",
        zIndex: 1,
      },
    },
  ];

  const ArmarSerieNumero = (serie: string, numero: number) => {
    let serieNumero = "";
    if (serie != "") serieNumero = serie;

    if (serieNumero != "") {
      if (numero) {
        serieNumero = serieNumero + " - " + numero.toString();
      }
    } else {
      if (numero) {
        serieNumero = numero.toString();
      }
    }
    return serieNumero;
  };
  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  const handleAnularPractica = (practica: PrecargaSempreList) => {
    setShowAlertModal(true);
    setPracticaSelected(practica);
    setTextAlertModal(
      `Está por anular la práctica ${practica.codigoPractica} - ${practica.nombrePractica} del paciente ${practica.afiliadoNombre} del día ${getFormattedDate(practica.fechaConsumo.toString())}. ¿Desea continuar?`
    );
  };

  const handlePresentarPractica = (practica: PrecargaSempreList) => {
    setShowPresentarModal(true);
    setPracticaSelected(practica);
  };

  const setColumnsToShow = () => {
    if (authInfo.role === "ADMIN") {
      const newColumn = {
        name: "Profesional",
        selector: (row: any) => row.efectorNombre,
        sortable: true,
        sortField: "efectornombre",
      };
      setColumns([newColumn, ...initialColumns]);
    } else setColumns(initialColumns);
  };

  useEffect(() => {
    if (location.search === "") {
      setQueryParams(initialFilter);
    }
    if (queryParams.page && authInfo) {
      getPracticas();
    }
    if (authInfo) {
      setColumnsToShow();
    }
  }, [queryParams, authInfo]);

  useEffect(() => {
    if (state?.textAlert) setShowAlertMessage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const getPracticas = async () => {
    setFetching(true);
    if (authInfo) {
      let params = { ...queryParams };
      if (authInfo.role === "PROFESIONAL" && authInfo.profesionalId) {
        params = { ...params, profesionalId: authInfo.profesionalId };
      }
      setFetching(true);
      importacionSempreServices
        .getList(params)
        .then((response) => {
          setFetching(false);
          setEntities(response.data.items);
          setTotalCount(response.data.totalItems);
        })
        .catch((error) => {});
    }
    // practicaServices
    //   .getList(queryParams)
    //   .then((response) => {
    //     setFetching(false);
    //     setEntities(response.data.items);
    //     setTotalCount(response.data.totalItems);
    //   })
    //   .catch((error) => {});
  };

  const [entities, setEntities] = useState<PrecargaSempreList[]>([]);

  const [totalCount, setTotalCount] = useState<number>(0);

  const anularPractica = () => {
    if (practicaSelected) {
      setFetching(true);
      const practica: any = {
        valOnlineNumero: practicaSelected.valOnlineNumero,
        codigoPractica: practicaSelected.codigoPractica,
      };

      importacionSempreServices
        .anular(practica)
        .then((response: any) => {
          setShowAlertModal(false);
          getPracticas();
          navigate(
            {
              pathname: ".",
              search,
            },
            {
              state: {
                typeAlert: "success",
                textAlert: `Se anuló la práctica del paciente ${response.data?.afiliadoNombre}.`,
              },
            }
          );
        })
        .catch(() => {
          setShowAlertModal(false);
          navigate(
            {
              pathname: ".",
              search,
            },
            {
              state: {
                typeAlert: "error",
                textAlert: "No se pudo anular la práctica. Intente nuevamente.",
              },
            }
          );

          setShowAlertMessage(true);
        });
    }
  };

  const ActionsComponent = () => {
    return (
      <>
        <Button
          className="close-button"
          onClick={(e) => {
            setShowAlertModal(false);
            setPracticaSelected(undefined);
          }}
        >
          Cancelar
        </Button>
        <Button
          className="create-button"
          variant="contained"
          onClick={() => anularPractica()}
        >
          Anular
        </Button>
      </>
    );
  };

  const refetch = () => {
    getPracticas();
  };

  const handleClosePresentarModal = (added: boolean, item: any) => {
    setShowPresentarModal(false);
    if (added) {
      getPracticas();
      navigate(
        {
          pathname: ".",
          search,
        },
        {
          state: {
            typeAlert: "success",
            textAlert: `Se agregó la práctica del paciente ${item.paciente}.`,
          },
        }
      );
    }
  };

  return (
    <div className="precarga-list-container">
      <AlertMessage
        text={state?.textAlert}
        type={state?.typeAlert}
        handleShowExternaly={true}
        show={showAlertMessage}
        setShow={setShowAlertMessage}
      />
      <div className="row button-section">
        <div className="w-50">
          <OutlinedInput
            type={"text"}
            placeholder="Búsqueda por nombre de afiliado, cuil o número de validación online"
            className={`custom-input`}
            autoComplete="off"
            onChange={(event) => {
              setQueryParams({
                ...queryParams,
                search:
                  event.target.value !== "" ? event.target.value : undefined,
              });
            }}
          ></OutlinedInput>
        </div>
        {/* <Button
          className="button"
          variant="contained"
          onClick={() => console.log("Confirmar")} //handleShowAddModal()}
        >
          Confirmar
        </Button>
        <Button
          className="button"
          variant="outlined"
          onClick={() => console.log("Anular")} //handleShowAddModal()}
        >
          Anular
        </Button> */}
      </div>
      <div className="row">
        <div className="practicas-table">
          <Table
            table={{
              columns,
              data: entities,
              paginationTotalRows: totalCount,
              pointerOnHover: true,
              highlightOnHover: true,
              // onRowClicked: handleView,
              // noDataComponent: !fetching && noDataComponent,
            }}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
            fixedHeader={true}
            fixedHeaderScrollHeight={true}
          ></Table>
        </div>
      </div>
      {!!showPresentarModal && practicaSelected && (
        <PracticaPresentarModal
          show={showPresentarModal}
          handleClose={handleClosePresentarModal}
          practica={practicaSelected}
          refetch={refetch}
        />
      )}
      {!!showAlertModal && (
        <AlertDialog
          title={"Advertencia"}
          text={textAlertModal}
          show={showAlertModal}
          setShow={setShowAlertModal}
          handleShowExternaly={true}
          actions={<ActionsComponent />}
        />
      )}
    </div>
  );
}
